<template>
  <div class="patrones-ocultos">
    <h3 class="text-2xl font-bold text-gray-800 mb-6">🔍 Patrones Ocultos Detectados por IA</h3>
    
    <!-- Correlaciones Detectadas -->
    <div v-if="correlaciones && correlaciones.length > 0" class="mb-8">
      <h4 class="text-xl font-bold text-gray-800 mb-4">🔗 Correlaciones Significativas</h4>
      <div class="space-y-4">
        <div
          v-for="(correlacion, index) in correlaciones"
          :key="index"
          :class="[
            'rounded-2xl p-6 border-l-4 shadow-lg hover:shadow-xl transition-all',
            correlacion.significancia === 'ALTA' ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-500' :
            'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-500'
          ]"
        >
          <div class="flex items-start justify-between mb-4">
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-2">
                <span class="text-2xl">{{ correlacion.significancia === 'ALTA' ? '⚡' : '💡' }}</span>
                <span :class="[
                  'px-3 py-1 rounded-full text-xs font-bold',
                  correlacion.significancia === 'ALTA' ? 'bg-purple-200 text-purple-800' : 'bg-blue-200 text-blue-800'
                ]">
                  {{ correlacion.significancia }}
                </span>
              </div>
              <div class="flex items-center gap-3 mb-3">
                <span class="px-3 py-1 bg-white rounded-lg font-semibold text-gray-700 text-sm">
                  {{ formatTag(correlacion.variables[0]) }}
                </span>
                <span class="text-2xl">↔️</span>
                <span class="px-3 py-1 bg-white rounded-lg font-semibold text-gray-700 text-sm">
                  {{ formatTag(correlacion.variables[1]) }}
                </span>
              </div>
              <p class="text-gray-700 font-medium">{{ correlacion.insight }}</p>
            </div>
            <div class="text-right ml-4">
              <div :class="[
                'text-4xl font-black',
                correlacion.correlacion > 0.7 ? 'text-green-600' :
                correlacion.correlacion > 0.5 ? 'text-blue-600' : 'text-yellow-600'
              ]">
                {{ Math.round(correlacion.correlacion * 100) }}%
              </div>
              <div class="text-xs text-gray-500">correlación</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Anomalías Detectadas -->
    <div v-if="anomalias && anomalias.length > 0" class="mb-8">
      <h4 class="text-xl font-bold text-gray-800 mb-4">📊 Anomalías Detectadas</h4>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="(anomalia, index) in anomalias"
          :key="index"
          :class="[
            'rounded-2xl p-6 border-2',
            anomalia.impacto === 'POSITIVO' ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'
          ]"
        >
          <div class="flex items-center gap-3 mb-3">
            <span class="text-3xl">{{ anomalia.impacto === 'POSITIVO' ? '📈' : '📉' }}</span>
            <div>
              <div class="text-sm text-gray-600">{{ anomalia.fecha }}</div>
              <div class="font-bold text-gray-900">{{ formatTag(anomalia.tipo) }}</div>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-3 mb-3">
            <div class="bg-white rounded-lg p-3">
              <div class="text-xs text-gray-600">Esperado</div>
              <div class="text-2xl font-bold text-gray-700">{{ anomalia.valor_esperado }}</div>
            </div>
            <div class="bg-white rounded-lg p-3">
              <div class="text-xs text-gray-600">Real</div>
              <div class="text-2xl font-bold" :class="anomalia.impacto === 'POSITIVO' ? 'text-green-600' : 'text-red-600'">
                {{ anomalia.valor_real }}
              </div>
            </div>
          </div>
          <div class="bg-white rounded-lg p-3">
            <div class="text-xs font-semibold text-gray-700 mb-1">Posible Causa</div>
            <div class="text-sm text-gray-600">{{ anomalia.posible_causa }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tendencias Emergentes -->
    <div v-if="tendencias && tendencias.length > 0" class="mb-8">
      <h4 class="text-xl font-bold text-gray-800 mb-4">🚀 Tendencias Emergentes</h4>
      <div class="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
        <div class="space-y-3">
          <div
            v-for="(tendencia, index) in tendencias"
            :key="index"
            class="flex items-start gap-3 p-4 bg-white rounded-xl hover:shadow-md transition-all"
          >
            <span class="text-2xl">📊</span>
            <div class="flex-1">
              <p class="text-gray-800 font-medium">{{ tendencia }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Insights Ocultos -->
    <div v-if="insights && insights.length > 0">
      <h4 class="text-xl font-bold text-gray-800 mb-4">💎 Insights Ocultos</h4>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="(insight, index) in insights"
          :key="index"
          class="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6 border border-yellow-200 hover:shadow-lg transition-all"
        >
          <div class="flex items-start gap-3">
            <span class="text-3xl">💡</span>
            <p class="text-gray-800 font-medium flex-1">{{ insight }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Patrones Temporales -->
    <div v-if="patronesTemporales" class="mt-8">
      <h4 class="text-xl font-bold text-gray-800 mb-4">⏰ Patrones Temporales</h4>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div class="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
          <div class="text-sm text-gray-600 mb-2">🕐 Mejor Hora</div>
          <div class="text-2xl font-bold text-blue-600">{{ patronesTemporales.mejor_hora_cotizacion }}</div>
          <div class="text-xs text-gray-500 mt-1">Para cotizaciones</div>
        </div>
        <div class="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
          <div class="text-sm text-gray-600 mb-2">📅 Mejor Día</div>
          <div class="text-2xl font-bold text-green-600">{{ patronesTemporales.mejor_dia_cierre }}</div>
          <div class="text-xs text-gray-500 mt-1">Para cerrar ventas</div>
        </div>
        <div class="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 md:col-span-2">
          <div class="text-sm text-gray-600 mb-2">📊 Patrón Semanal</div>
          <div class="text-sm font-semibold text-purple-600">{{ patronesTemporales.patron_semanal }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PatronesOcultos',
  props: {
    patrones: {
      type: Object,
      required: true
    }
  },
  computed: {
    correlaciones() {
      return this.patrones?.correlaciones_detectadas || []
    },
    anomalias() {
      return this.patrones?.anomalias_detectadas || []
    },
    tendencias() {
      return this.patrones?.tendencias_emergentes || []
    },
    insights() {
      return this.patrones?.insights_ocultos || []
    },
    patronesTemporales() {
      return this.patrones?.patrones_temporales || null
    }
  },
  methods: {
    formatTag(tag) {
      return tag.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  }
}
</script>
